import { Dir } from '@angular/cdk/bidi';
import { CurrencyPipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, ElementRef, inject, model, OnDestroy, signal, viewChild, ViewEncapsulation } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatAccordion, MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle } from '@angular/material/expansion';
import { MatIcon } from '@angular/material/icon';
import { MatSelectionList } from '@angular/material/list';
import { MatTooltip } from '@angular/material/tooltip';
import { ModalService } from '@common/components/modal/modal.service';
import { PushPipe } from '@ngrx/component';
import { environment } from '@src/environments/environment';
import { AppService } from '@src/suite/app.service';
import { PSTFieldNames } from '@src/suite/common/data-models/fields/pst-field-names';
import { FieldDefValidations } from '@src/suite/common/enums/enums';
import { NomWebServicesJson } from '@src/suite/common/services/nom-web-services-json';
import { ViewStateService } from '@src/suite/common/services/view-state-service';
import { StFullScreenComponent } from '@src/suite/common/st-full-screen-dialog/full-screen/full-screen';
import { selectEnforceRequiredFieldsOnCreate } from '@src/suite/common/store/global-tables/global-tables.selectors';
import { selectViewStateSearchString } from '@src/suite/common/store/navigation/selectors/viewstate.selectors';
import { RecordHttpActions, StoriesRecordActions } from '@src/suite/common/store/records/records.actions';
import { calculateRequiredFieldGroups, getTypeInternal } from '@src/suite/common/utils/record-utils';
import { PlaybookService } from '@src/suite/playbook/services/playbook-service';
import { DateTime } from 'luxon';
import { Subject } from 'rxjs';

import { AttachmentsListComponent } from '../../../components/attachments-list/attachments-list';
import { BetaComponent } from '../../../components/beta/st-beta.component';
import { AddressFormComponent } from '../../../controls/address-form/address-form';
import { CheckBoxComponent } from '../../../controls/check-box/check-box';
import { CheckListComponent } from '../../../controls/check-list/check-list';
import { ComboBoxComponent } from '../../../controls/combo-box/combo-box';
import { DatePickerComponent } from '../../../controls/date-picker/date-picker';
import { DateTimePickerComponent } from '../../../controls/date-time-picker/date-time-picker';
import { DateTimeRangeComponent } from '../../../controls/date-time-range/date-time-range';
import { DurationSecondsInputFieldComponent } from '../../../controls/duration-seconds-input-field/duration-seconds-input-field';
import { SpecialComponent } from '../../../controls/special/special';
import { SpecialCostComponent } from '../../../controls/special/special-cost/special-cost';
import { StTextAreaComponent } from '../../../controls/st-text-area/st-text-area';
import { StaffIDListMultipleComponent } from '../../../controls/staff-id-list/staff-id-list-multiple';
import { TextFieldComponent } from '../../../controls/text-field/text-field';
import { TreeComponent } from '../../../controls/tree/tree';
import { ClickStopPropagationDirective } from '../../../directives/stop-propagation';

@Component({
    selector: 'st-story-full-screen-properties',
    imports: [
        AddressFormComponent,
        AttachmentsListComponent,
        BetaComponent,
        CheckBoxComponent,
        CheckListComponent,
        ClickStopPropagationDirective,
        ComboBoxComponent,
        CurrencyPipe,
        DatePickerComponent,
        DateTimePickerComponent,
        DateTimeRangeComponent,
        Dir,
        DurationSecondsInputFieldComponent,
        MatAccordion,
        MatExpansionPanel,
        MatExpansionPanelHeader,
        MatExpansionPanelTitle,
        MatIcon,
        MatIconButton,
        MatTooltip,
        PushPipe,
        SpecialComponent,
        SpecialCostComponent,
        StaffIDListMultipleComponent,
        StTextAreaComponent,
        TextFieldComponent,
        TreeComponent,
    ],
    templateUrl: './st-story-full-screen-properties.html',
    styleUrl: './st-story-full-screen-properties.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-story-full-screen-properties',
    },
})
export class StStoryFullScreenPropertiesComponent extends StFullScreenComponent implements OnDestroy {
    // services
    public changeDetectorRef = inject(ChangeDetectorRef);
    public modalService = inject(ModalService);
    public appService = inject(AppService);
    public playbookService = inject(PlaybookService);
    public viewStateService = inject(ViewStateService);
    protected dialog = inject(MatDialog);
    nomWebServicesJson = inject(NomWebServicesJson);
    elementRef = inject(ElementRef);
    environment = environment;

    // computed
    readonly storyEditable = computed(() => {
        if (!this.data().isWriteable) return false;
        if (this.data().isDisabled) return false;
        return true;
    });

    // TODO: FIX THIS
    readonly specialProps = computed(() => {
        return this.globalTablesService.getSpecialPropertiesFor(this.getType()).filter(field => field.ID !== PSTFieldNames.PSTExternalFiles && field.ID !== PSTFieldNames.Relationships);
    });

    readonly requiredFieldGroups = computed(() => {
        return calculateRequiredFieldGroups(this.data(), this.playbookService);
    });

    override readonly getType = computed(() => {
        return getTypeInternal(this.data()?.Type);
    });

    readonly showAttachments = computed(() => {
        return this.appService.attachmentsAllowed && !!this.data()?.Guid && environment?.demoFlag;
    });

    // signals
    override readonly expandedGroup = signal<string>('SCRIPT');

    // store selector signals
    searchString$ = this.store.select(selectViewStateSearchString);
    enforceRequiredFieldsOnCreate = this.store.selectSignal(selectEnforceRequiredFieldsOnCreate);

    // models
    override readonly data = model<StorytellingStoryRecord>();

    // view child
    readonly selectionList = viewChild(MatSelectionList);

    // props
    destroyed$ = new Subject<void>();
    unSavedChanges: boolean;
    pstFieldNames = PSTFieldNames;
    fieldDefValidations = FieldDefValidations;

    // getters
    get isRTL() {
        return this.globalTablesService.getRtl();
    }

    // lifecycle hooks
    ngOnDestroy() {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    public getList(group: string): FieldDef[] {
        return this.globalTablesService.getDynamicPropertiesFor(this.getType(), group).filter(p => p.ID !== PSTFieldNames.PSTTeamLocation);
    }

    updateStory(value: any, fieldId: string) {
        const fieldDef = [{ value, key: fieldId }];
        // TODO: not cool to have this title logic here
        if (fieldId === PSTFieldNames.Title) {
            this.data().Title = value.trim();
        }
        if (fieldId === PSTFieldNames.PSTTopicEventStartDate) {
            const castedVal = value as DateTime;
            if (castedVal > this.data()?.EventEnd) {
                fieldDef.push({ value: castedVal, key: PSTFieldNames.PSTTopicEventEndDate });
            }
        }
        this.store.dispatch(RecordHttpActions.saveRecordChanges({ record: this.data(), fieldsToUpdate: fieldDef }));
    }

    timezoneChanged(fieldId: string, event: any) {
        this.data.update(val => {
            val[fieldId] = event;
            return val;
        });
        this.store.dispatch(StoriesRecordActions.updateStories({ records: [this.data()] }));
    }
}
